import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ChevronLeft, ChevronRight, Filter, RefreshCw } from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import FlagBadge from '../../components/moderation/FlagBadge';
import VisibilityBadge from '../../components/moderation/VisibilityBadge';
import { isPhotoPublic, photoSignature, timeAgo } from '../../utils/moderationFormat';

const PER_PAGE = 25;

const QueuePage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [fireFilter, setFireFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('all'); // all | green | red
  const [visibilityFilter, setVisibilityFilter] = useState('all'); // all | public | operational
  const [sort, setSort] = useState('red'); // red | newest | oldest

  const { data, isLoading, isFetching, refetch } = useQuery(
    ['moderation-queue', page],
    () => apiService.listModerationPhotos({ status: 'pending', page, per_page: PER_PAGE }),
    { refetchInterval: 30_000, keepPreviousData: true },
  );

  const filtered = useMemo(() => {
    const photos = data?.data || [];
    let out = photos;
    if (fireFilter.trim()) {
      const q = fireFilter.trim().toLowerCase();
      out = out.filter((p) => (p.fire_id || '').toLowerCase().includes(q));
    }
    if (levelFilter === 'green') {
      out = out.filter((p) => (p.flags || []).length === 0);
    } else if (levelFilter === 'red') {
      out = out.filter((p) => (p.metrics?.red_count || 0) > 0);
    }
    if (visibilityFilter === 'public') {
      out = out.filter((p) => isPhotoPublic(p));
    } else if (visibilityFilter === 'operational') {
      out = out.filter((p) => !isPhotoPublic(p));
    }
    if (sort === 'newest') {
      out = [...out].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    } else if (sort === 'oldest') {
      out = [...out].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    }
    // 'red' is the backend default ordering (already sorted by red_count desc).
    return out;
  }, [data, fireFilter, levelFilter, visibilityFilter, sort]);

  const total = data?.meta?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Moderação de Fotos
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {total} pendentes — auto-refresh a cada 30s
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-gray-500" />
        <input
          value={fireFilter}
          onChange={(e) => setFireFilter(e.target.value)}
          placeholder="Filtrar por fire_id…"
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">Todas as flags</option>
          <option value="green">Sem flags (fast-track)</option>
          <option value="red">Com flags vermelhas</option>
        </select>
        <select
          value={visibilityFilter}
          onChange={(e) => setVisibilityFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">Todas as visibilidades</option>
          <option value="public">Só públicas</option>
          <option value="operational">Só operacionais</option>
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="red">Mais flags vermelhas</option>
          <option value="newest">Mais recentes</option>
          <option value="oldest">Mais antigas</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-12 text-center text-gray-500">
          Nada para moderar.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((p) => (
            <Link
              key={p.id}
              to={`/moderation/photos/${p.id}`}
              className="block bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden hover:shadow-lg transition"
            >
              <div className="relative aspect-[4/3] bg-gray-100 dark:bg-gray-900">
                <img
                  src={`${p.url}${p.url.includes('?') ? '&' : '?'}cacheBuster=${Date.now()}-${Math.random().toString(36).slice(2)}`}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                {(p.metrics?.red_count || 0) > 0 && (
                  <div className="absolute top-2 left-2 bg-fire-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {p.metrics.red_count} 🚩
                  </div>
                )}
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-mono truncate">{p.fire_id || '—'}</span>
                  <span>{timeAgo(p.created_at)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <VisibilityBadge photo={p} compact />
                  {photoSignature(p) && (
                    <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[120px]">
                      por {photoSignature(p)}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {(p.flags || []).slice(0, 3).map((f, i) => (
                    <FlagBadge key={i} flag={f} compact />
                  ))}
                  {(p.flags || []).length > 3 && (
                    <span className="text-xs text-gray-500">+{p.flags.length - 3}</span>
                  )}
                  {(p.flags || []).length === 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200">
                      Sem flags
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="p-2 border rounded-md disabled:opacity-50 dark:border-gray-600"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="p-2 border rounded-md disabled:opacity-50 dark:border-gray-600"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default QueuePage;
